import {AxiosInstance} from "axios";
import {ColumnDefinition} from "../model/workspace/ColumnDefinition";
import {
	CreateWorkspaceViewApiDto,
	QuerySearchFilterApiDto,
	WorkspaceDataApiDto,
	WorkspaceViewApiDto,
	WorkspaceViewsApiDto
} from "./model/rest";
import {WorkspaceView} from "../model/workspace/WorkspaceView";
import {none, option} from "ts-option";
import {ViewColumn} from "../model/workspace/ViewColumn";
import WorkspaceContainer, {WorkspaceName} from "../components/v5/table/workspace/WorkspaceContainer";
import WorkspaceViewsData from "../model/workspace/WorkspaceViewsData";
import Filter from "../model/workspace/Filter";
import _ = require("underscore");

const uuidv4 = require("uuid/v4");
const qs = require('qs');

export default class WorkspaceApi {
	static ACCOUNTING_WORKSPACES = [WorkspaceName.Transactions, WorkspaceName.Bills];

	constructor(public readonly axiosInstance: AxiosInstance) { }

	/**
	 * Fetch user views and default view
	 * @param {string} workspaceName
	 * @returns {Promise<WorkspaceViewsData>}
	 */
	fetchWorkspaceViews(workspaceName: WorkspaceName): Promise<WorkspaceViewsData> {
		return Promise.all([this.fetchUserViews(workspaceName), this.fetchColumnDefinitions(workspaceName)]).then((values) => {
			const columnDefinitions = values[1];
			const defaultViewColumns = columnDefinitions.map(
				(cd, index) => new ViewColumn(`${index}`, cd, cd.defaultVisible, none));
			const defaultView = new WorkspaceView("default", `All ${workspaceName.replaceUnderscoresWithSpaces()}`,
				defaultViewColumns, [], true);
			const userViews = values[0].views.map(v => {
				const viewColumns: ViewColumn[] = v.columns.map(
					vc => new ViewColumn(vc.id,
						columnDefinitions.filter(cd => cd.id == vc.columnDefinitionId)[0],
						vc.visible,
						option(vc.width)));
				const filters = v.filters.map(
					f =>
						Filter.fromJson(f, columnDefinitions.filter(cd => cd.id == f.columnDefinitionId)[0])
				);
				// Add columns created after filter save
				const columns = columnDefinitions.filter(cd => _.findIndex(viewColumns, (vc) => vc.columnDefinition.id === cd.id) == -1)
					.map(item => new ViewColumn(uuidv4(), item, item.defaultVisible, none));
				return new WorkspaceView(v.id, v.name, viewColumns.concat(columns), filters, v.global);

			});
			return new WorkspaceViewsData([defaultView].concat(userViews), values[0].favourites);
		})
	}

	/**
	 * Fetch view
	 * @param {WorkspaceName} workspaceName
	 * @returns {Promise<WorkspaceView>}
	 */
	fetchWorkspaceLiteView(workspaceName: WorkspaceName): Promise<WorkspaceView> {
		return this.fetchColumnDefinitions(workspaceName).then((columnDefinitions) => {
			const viewColumns = columnDefinitions.map(
				(cd, index) => new ViewColumn(`${index}`, cd, cd.defaultVisible, none));
			const view = new WorkspaceView("default", `All ${workspaceName}`, viewColumns, [], true);
			return view;
		});
	}

	/**
	 *  Fetch data
	 * @param {string} workspaceName
	 * @param page
	 * @param filters
	 * @param sort
	 * @param size
	 * @returns {Promise<WorkspaceDataApiDto>} Promise containing all data.
	 */
	fetchWorkspaceData(workspaceName: WorkspaceName,
		page: number,
		filters: QuerySearchFilterApiDto[],
		sort: string | string[],
		size: number = WorkspaceContainer.PAGE_SIZE): Promise<WorkspaceDataApiDto> {
		const params = {
			params: {page: page - 1, size, sort},
			paramsSerializer: (params: string[]) => qs.stringify(params, {arrayFormat: 'repeat'})
		};
		const url = `${this.getApiUrl(workspaceName)}/${workspaceName}/data`;
		return this.axiosInstance.post(url, filters, params).then(resp => resp.data);
	}

	/**
	 * Save view
	 * @param {string} workspaceName
	 * @param viewName
	 * @param favourite
	 * @param columns
	 * @param filters
	 * @param global
	 * @returns {Promise<WorkspaceView>}
	 */
	saveView(workspaceName: WorkspaceName,
		viewName: string,
		favourite: boolean,
		global: boolean,
		columns: ViewColumn[],
		filters: Filter[]): Promise<WorkspaceView> {
		const url = `${this.getApiUrl(workspaceName)}/views/${workspaceName}`;

		const json: CreateWorkspaceViewApiDto = {
			name: viewName,
			favourite: favourite,
			columns: columns.map(c => {
				return {
					columnDefinitionId: c.columnDefinition.id,
					visible: c.visible,
					width: c.width.orNull
				}
			}),
			filters: filters.map(f => f.toCreateJson()),
			global: global
		};
		return this.axiosInstance.post(url, json).then(resp => {
				const data: WorkspaceViewApiDto = resp.data;
				const col = columns.map(
					c => c.copy({id: data.columns.filter(dc => dc.columnDefinitionId == c.columnDefinition.id)[0].id}));
				const fil = filters.map(
					f => f.copy({id: data.filters.filter(df => df.columnDefinitionId == f.columnDefinition.id)[0].id}));
				return new WorkspaceView(data.id, data.name, col, fil, data.global);
			}
		)
	}

	/**
	 * Rename view
	 * @param {string} viewId
	 * @param {string} name
	 * @param workspaceName
	 * @returns {Promise<any>}
	 */
	renameView(viewId: string, name: string, workspaceName: WorkspaceName): Promise<any> {
		const url = `${this.getApiUrl(workspaceName, workspaceName === WorkspaceName.Jobs)}/views/${viewId}`;
		return this.axiosInstance.put(url, {name});
	}

	/**
	 * Remove view from favourites
	 * @param {string} viewId
	 * @param workspaceName
	 * @returns {Promise<any>}
	 */
	removeFromFavourites(viewId: string, workspaceName: WorkspaceName): Promise<any> {
		const url = `${this.getApiUrl(workspaceName, workspaceName === WorkspaceName.Jobs)}/views/${viewId}/favourite`;
		return this.axiosInstance.delete(url);
	}

	/**
	 * Add view to favourites
	 * @param {string} viewId
	 * @param workspaceName
	 * @returns {Promise<any>}
	 */
	addViewToFavourites(viewId: string, workspaceName: WorkspaceName): Promise<any> {
		const url = `${this.getApiUrl(workspaceName, workspaceName === WorkspaceName.Jobs)}/views/${viewId}/favourite`;
		return this.axiosInstance.put(url);
	}

	/**
	 * Update favourite views order
	 * @param workspaceName
	 * @param viewIds
	 * @returns {Promise<any>}
	 */
	reorderFavouritesViews(workspaceName: WorkspaceName, viewIds: string[]): Promise<any> {
		const url = `${this.getApiUrl(workspaceName)}/views/${workspaceName}/order`;
		return this.axiosInstance.put(url, viewIds);
	}

	/**
	 * Delete view
	 * @param {string} viewId
	 * @param workspaceName
	 * @returns {Promise<any>}
	 */
	deleteView(viewId: string, workspaceName: WorkspaceName): Promise<any> {
		const url = `${this.getApiUrl(workspaceName, workspaceName === WorkspaceName.Jobs)}/views/${viewId}`;
		return this.axiosInstance.delete(url);
	}

	/**
	 * Returns start part of proper workspace url.
	 * @param {string} workspaceName
	 * @param addWorkspaceName
	 * @returns {string}
	 */
	getApiUrl(workspaceName: WorkspaceName, addWorkspaceName = false) {
		if (WorkspaceApi.ACCOUNTING_WORKSPACES.indexOf(workspaceName) > -1) {
			return `/api/web/accounting${addWorkspaceName ? `/${workspaceName}` : ""}`;
		}
		return `/api/web${addWorkspaceName ? `/${workspaceName}` : ""}`
	}

	/**
	 * Fetch column definitions
	 * @param {string} workspaceName
	 * @returns {Promise<ColumnDefinition[]>}
	 */
	private fetchColumnDefinitions(workspaceName: WorkspaceName): Promise<ColumnDefinition[]> {
		const url = `${this.getApiUrl(workspaceName)}/views/${workspaceName}/column_definitions`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(ColumnDefinition.fromJson));
	}

	/**
	 * Fetch user views.
	 * @param {string} workspaceName
	 * @returns {Promise<WorkspaceViewsApiDto>} Promise containing all user views.
	 */
	private fetchUserViews(workspaceName: WorkspaceName): Promise<WorkspaceViewsApiDto> {
		const url = `${this.getApiUrl(workspaceName)}/views/${workspaceName}`;
		return this.axiosInstance.get(url).then(resp => resp.data);
	}
}
